import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppHeader from "@/components/app/AppHeader";
import AppSection from "@/components/app/AppSection";
import EmptyState from "@/components/app/EmptyState";
import { StatusBadge } from "@/components/app/StatusBadge";
import { api, formatApiError } from "@/lib/api";
import { Compass, Library, Check, HelpCircle, X } from "lucide-react";

/**
 * AppExplorer — Phase 2 base for the buyer flow.
 *
 * Navigation : TCG → Block → Set → Cards
 * Per card variant: 3 states  J'ai (have) / Manquant (missing) / Je ne sais pas (unknown)
 * No mission creation here — that's Phase 3.
 */
export default function AppExplorer() {
    const [step, setStep] = useState("tcg"); // tcg | block | set | cards
    const [tcgs, setTcgs] = useState([]);
    const [activeTcg, setActiveTcg] = useState(null);
    const [blocks, setBlocks] = useState([]);
    const [activeBlock, setActiveBlock] = useState(null);
    const [sets, setSets] = useState([]);
    const [activeSet, setActiveSet] = useState(null);
    const [setDetail, setSetDetail] = useState(null);
    const [states, setStates] = useState({}); // key `${card_id}:${variant}` -> state
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/catalog/tcgs").then(({ data }) => setTcgs(data)).catch((e) => setError(formatApiError(e)));
    }, []);

    async function pickTcg(t) {
        setActiveTcg(t); setStep("block"); setError("");
        try { const { data } = await api.get(`/catalog/tcgs/${t.slug}/blocks`); setBlocks(data); }
        catch (e) { setError(formatApiError(e)); }
    }
    async function pickBlock(b) {
        setActiveBlock(b); setStep("set"); setError("");
        try { const { data } = await api.get(`/catalog/blocks/${b._id}/sets`); setSets(data); }
        catch (e) { setError(formatApiError(e)); }
    }
    const loadSet = useCallback(async (s) => {
        setActiveSet(s); setStep("cards"); setError(""); setSetDetail(null); setStates({});
        try {
            const [{ data: detail }, { data: myStates }] = await Promise.all([
                api.get(`/catalog/sets/${s._id}`),
                api.get(`/catalog/sets/${s._id}/state`),
            ]);
            setSetDetail(detail);
            const m = {};
            myStates.forEach((st) => { m[`${st.card_id}:${st.variant_type}`] = st.state; });
            setStates(m);
        } catch (e) { setError(formatApiError(e)); }
    }, []);

    async function setState(card, variant, state) {
        const key = `${card._id}:${variant}`;
        const prev = states[key];
        setStates((s) => ({ ...s, [key]: state }));
        try { await api.post("/catalog/card-state", { card_id: card._id, variant_type: variant, state }); }
        catch (e) {
            setStates((s) => ({ ...s, [key]: prev }));
            setError(formatApiError(e));
        }
    }

    function back() {
        if (step === "cards") { setStep("set"); setActiveSet(null); setSetDetail(null); }
        else if (step === "set") { setStep("block"); setActiveBlock(null); setSets([]); }
        else if (step === "block") { setStep("tcg"); setActiveTcg(null); setBlocks([]); }
    }

    const title = step === "tcg" ? "Choisir un jeu" : step === "block" ? activeTcg?.name : step === "set" ? activeBlock?.name : activeSet?.name;
    const subtitle = step === "cards" && setDetail ? `${setDetail.cards.length} cartes · ${setDetail.categories.length} catégories` : "Explorer";

    return (
        <>
            <AppHeader title={title || "Explorer"} subtitle={subtitle} showBack={step !== "tcg"} onBack={back} />

            {error && (
                <AppSection><div className="rounded-lg border border-md-danger/30 bg-md-danger/10 px-3 py-2 text-xs text-md-danger">{error}</div></AppSection>
            )}

            {step === "tcg" && (
                <AppSection>
                    {tcgs.length === 0 ? (
                        <EmptyState icon={<Library className="h-6 w-6" />} title="Aucun jeu actif" description="Demande à l'admin d'activer un TCG." />
                    ) : (
                        <div className="space-y-3">
                            {tcgs.map((t) => (
                                <button
                                    key={t._id}
                                    onClick={() => pickTcg(t)}
                                    data-testid={`tcg-${t.slug}`}
                                    className="flex w-full items-center justify-between rounded-2xl border border-md-border bg-md-surface p-5 text-left hover:border-md-secondary/50"
                                >
                                    <div>
                                        <p className="md-overline">{t.locale}</p>
                                        <p className="mt-1 text-lg font-bold tracking-tight text-md-text">{t.name}</p>
                                    </div>
                                    <Compass className="h-5 w-5 text-md-text-2" />
                                </button>
                            ))}
                        </div>
                    )}
                </AppSection>
            )}

            {step === "block" && (
                <AppSection>
                    {blocks.length === 0 ? (
                        <EmptyState icon={<Library className="h-6 w-6" />} title="Aucun bloc actif" description="L'admin n'a pas encore activé de bloc pour ce jeu." />
                    ) : (
                        <div className="grid grid-cols-1 gap-3">
                            {blocks.map((b) => (
                                <button
                                    key={b._id}
                                    onClick={() => pickBlock(b)}
                                    data-testid={`block-${b.external_id}`}
                                    className="flex w-full items-center gap-4 rounded-2xl border border-md-border bg-md-surface p-4 text-left hover:border-md-secondary/50"
                                >
                                    {b.logo_url && <img src={b.logo_url} alt="" className="h-10 w-16 object-contain" />}
                                    <div className="flex-1 min-w-0">
                                        <p className="md-overline">Bloc</p>
                                        <p className="mt-1 truncate font-semibold text-md-text">{b.name}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </AppSection>
            )}

            {step === "set" && (
                <AppSection>
                    {sets.length === 0 ? (
                        <EmptyState icon={<Library className="h-6 w-6" />} title="Aucun set importé" description="Aucun set n'a encore été importé pour ce bloc." />
                    ) : (
                        <div className="grid grid-cols-2 gap-3">
                            {sets.map((s) => (
                                <button
                                    key={s._id}
                                    onClick={() => loadSet(s)}
                                    data-testid={`set-${s.external_id}`}
                                    className="flex flex-col gap-2 rounded-2xl border border-md-border bg-md-surface p-4 text-left hover:border-md-secondary/50"
                                >
                                    {s.logo_url && <img src={s.logo_url} alt="" className="h-14 w-full object-contain" />}
                                    <p className="text-xs font-semibold text-md-text">{s.name}</p>
                                    <p className="text-[10px] text-md-text-2">{s.card_count_total} cartes</p>
                                </button>
                            ))}
                        </div>
                    )}
                </AppSection>
            )}

            {step === "cards" && setDetail && (
                <CardsView setDetail={setDetail} states={states} setState={setState} />
            )}
        </>
    );
}

function CardsView({ setDetail, states, setState }) {
    const [variantFilter, setVariantFilter] = useState("all");
    const counts = useMemo(() => {
        const c = { have: 0, missing: 0, unknown: 0, total: 0 };
        setDetail.cards.forEach((card) => {
            (card.variants || []).forEach((v) => {
                c.total += 1;
                const s = states[`${card._id}:${v}`];
                if (s) c[s] += 1;
            });
        });
        return c;
    }, [setDetail, states]);

    const variants = useMemo(() => {
        const s = new Set();
        setDetail.cards.forEach((c) => (c.variants || []).forEach((v) => s.add(v)));
        return ["all", ...Array.from(s)];
    }, [setDetail]);

    return (
        <>
            <AppSection>
                <div className="rounded-2xl border border-md-border bg-md-surface p-4">
                    <div className="grid grid-cols-4 gap-2 text-center">
                        <Stat label="Total" value={counts.total} tone="neutral" />
                        <Stat label="J'ai" value={counts.have} tone="success" />
                        <Stat label="Manquant" value={counts.missing} tone="primary" />
                        <Stat label="?" value={counts.unknown} tone="gold" />
                    </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2" role="tablist">
                    {variants.map((v) => (
                        <button
                            key={v}
                            onClick={() => setVariantFilter(v)}
                            data-testid={`variant-filter-${v}`}
                            className={`rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wider ${variantFilter === v ? "bg-md-primary text-md-primary-fg" : "border border-md-border bg-md-surface text-md-text-2 hover:bg-md-surface-2"}`}
                        >
                            {v === "all" ? "Tout" : v}
                        </button>
                    ))}
                </div>
            </AppSection>

            <AppSection>
                <div className="space-y-3">
                    {setDetail.cards.map((card) => (
                        <CardRow
                            key={card._id}
                            card={card}
                            states={states}
                            setState={setState}
                            variantFilter={variantFilter}
                        />
                    ))}
                </div>
            </AppSection>
        </>
    );
}

function Stat({ label, value, tone }) {
    return (
        <div>
            <p className="md-overline">{label}</p>
            <p className="mt-1 text-2xl font-black tracking-tight text-md-text">{value}</p>
        </div>
    );
}

function CardRow({ card, states, setState, variantFilter }) {
    const visibleVariants = (card.variants || []).filter((v) => variantFilter === "all" || v === variantFilter);
    if (visibleVariants.length === 0) return null;
    return (
        <div className="rounded-2xl border border-md-border bg-md-surface p-4">
            <div className="flex items-start gap-3">
                {card.image_url && (
                    <img src={`${card.image_url}/low.webp`} alt="" className="h-20 w-14 rounded-md object-cover" loading="lazy" />
                )}
                <div className="flex-1 min-w-0">
                    <p className="md-overline">N° {card.local_id} · {card.rarity_label || "—"}</p>
                    <p className="truncate text-base font-bold tracking-tight text-md-text">{card.name}</p>
                </div>
            </div>
            <div className="mt-3 space-y-2">
                {visibleVariants.map((v) => {
                    const key = `${card._id}:${v}`;
                    const s = states[key];
                    return (
                        <div key={v} className="flex items-center justify-between gap-2">
                            <span className="rounded bg-md-surface-2 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-md-text-2">{v}</span>
                            <div className="flex gap-1">
                                <StateBtn current={s} target="have" onClick={() => setState(card, v, "have")} icon={<Check className="h-3 w-3" />} label="J'ai" testId={`state-have-${card.local_id}-${v}`} />
                                <StateBtn current={s} target="missing" onClick={() => setState(card, v, "missing")} icon={<X className="h-3 w-3" />} label="Manquant" testId={`state-missing-${card.local_id}-${v}`} />
                                <StateBtn current={s} target="unknown" onClick={() => setState(card, v, "unknown")} icon={<HelpCircle className="h-3 w-3" />} label="?" testId={`state-unknown-${card.local_id}-${v}`} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function StateBtn({ current, target, onClick, icon, label, testId }) {
    const active = current === target;
    const cls = active
        ? (target === "have" ? "bg-md-accent text-white" :
           target === "missing" ? "bg-md-primary text-md-primary-fg" :
           "bg-md-secondary text-md-secondary-fg")
        : "border border-md-border bg-transparent text-md-text-2 hover:bg-md-surface-2";
    return (
        <button onClick={onClick} data-testid={testId} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${cls}`}>
            {icon}<span>{label}</span>
        </button>
    );
}
